import React, { useRef, useState } from 'react';
import { Share2, Check, MessageCircle } from 'lucide-react';
import { inviteMessage, whatsAppUrl } from '../lib/joinLink';

/**
 * Send somebody a game's or a league's join link (backlog item 89).
 *
 * Two buttons. **Share** opens the phone's own share sheet, which covers WhatsApp, Messenger,
 * Teams and the rest in one tap, and copies the message on a desktop where there is none.
 * **WhatsApp** goes straight there, because that is where these groups live and the owner asked
 * for it by name.
 *
 * Open to anybody who can see the code, not just the manager: getting people in is everyone's
 * job, and the manager can remove anyone they don't want.
 */
function ShareInvite({ kind, name, code, straightIn, className }) {
  return (
    <ShareButtons
      title={name}
      message={inviteMessage({ kind, name, code, straightIn })}
      className={className}
    />
  );
}

/**
 * Share and WhatsApp for any finished message - an invite, or one of the Gaffer's posts
 * (backlog item 90). `label` names what is being shared for a screen reader where a page has
 * several of these, one per post.
 */
export function ShareButtons({ title, message, className, label, prepareFile }) {
  const [done, setDone] = useState('');
  const fileRef = useRef(null);

  /**
   * `prepareFile` (backlog item 92) makes a picture to send with the message - a Gaffer tile.
   * Started when a finger first touches the button rather than on the tap itself, because a
   * phone only lets a page open the share sheet for a moment after the tap, and drawing the
   * picture inside that moment can use it up.
   */
  const prepare = () => {
    if (prepareFile && !fileRef.current) {
      fileRef.current = Promise.resolve()
        .then(prepareFile)
        .catch(() => null);
    }
    return fileRef.current || Promise.resolve(null);
  };

  // The picture, with the message as its caption. True if the share sheet took it, or the
  // person dismissed it; false to fall through to sharing the text.
  const shareWithPicture = async () => {
    const file = await prepare();
    if (!file || !navigator.canShare || !navigator.canShare({ files: [file] })) return false;
    try {
      await navigator.share({ title, text: message, files: [file] });
    } catch (err) {
      if (err && err.name === 'NotAllowedError') {
        // Drawing took longer than the phone allows after a tap. The picture is ready now,
        // so a second tap shares it at once.
        setDone('Picture ready - tap Share again');
        setTimeout(() => setDone(''), 4000);
        return true;
      }
      if (!(err && err.name === 'AbortError')) return false;
    }
    return true;
  };

  const share = async () => {
    if (prepareFile && navigator.share && await shareWithPicture()) return;
    if (navigator.share) {
      try {
        await navigator.share({ title, text: message });
        return;
      } catch (err) {
        // Dismissing the sheet rejects. That is not a failure and must not fall
        // through to a silent clipboard write the user never asked for.
        if (err && err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(message);
      setDone('Copied — paste it to your mates');
    } catch {
      setDone('Copy failed — the code is above');
    }
    setTimeout(() => setDone(''), 2500);
  };

  return (
    <span className={className ? `share-league ${className}` : 'share-league'}>
      <button
        type="button"
        className="primary-btn"
        onClick={share}
        onPointerDown={prepare}
        onFocus={prepare}
        aria-label={label ? `Share ${label}` : undefined}
      >
        {done ? <Check size={16} /> : <Share2 size={16} />}
        Share
      </button>
      <a
        className="secondary-btn share-whatsapp"
        href={whatsAppUrl(message)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label ? `Send ${label} on WhatsApp` : undefined}
      >
        <MessageCircle size={16} />
        WhatsApp
      </a>
      {done && <span className="share-feedback">{done}</span>}
    </span>
  );
}

export default ShareInvite;
